import { Mail, Phone, MapPin, Youtube, Facebook, Instagram, Calendar, Clock, Users, Tv } from "lucide-react";

// Resolve any image URL — backend returns "/api/files/..." which needs the
// API host prepended when not served from the same origin.
const resolveSrc = (src) => {
  if (!src) return "";
  if (src.startsWith("/api/")) {
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}${src}`;
  }
  return src;
};

const fmtTime = (t) => {
  if (!t) return "";
  // Already formatted (e.g. "10:00 AM") — pass through; otherwise leave 24h.
  return t;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" }); } catch { return iso; }
};

const composeAddress = (row) => {
  const parts = [row.venue_name, row.address_line1, row.address_line2, row.city, row.postcode, row.country].filter(Boolean);
  return parts.join(", ");
};

export default function Traditional({ church }) {
  const c = church || {};
  const ct = c.content || {};
  const primary = c.brand_primary_color || "#D4AF37";
  const accent = c.brand_accent_color || "#0A0A0A";

  const churchAddress = [c.address, c.address_line2, c.city, c.postcode, c.country].filter(Boolean).join(", ");

  return (
    <div data-testid="tpl-traditional" className="min-h-screen text-white" style={{ background: "#0A0A0A" }}>
      {/* HEADER */}
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {c.logo_url ? (
              <img src={resolveSrc(c.logo_url)} alt="" className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full flex items-center justify-center font-serif-display text-lg" style={{ background: primary, color: accent }}>
                {(c.name || "C").charAt(0)}
              </div>
            )}
            <div>
              <div className="font-serif-display text-xl">{c.name}</div>
              {c.denomination && <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-400">{c.denomination}</div>}
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-xs tracking-widest uppercase text-neutral-300">
            <a href="#about" className="hover:opacity-80">About</a>
            <a href="#services" className="hover:opacity-80">Services</a>
            <a href="#activities" className="hover:opacity-80">Activities</a>
            <a href="#events" className="hover:opacity-80">Events</a>
            <a href="#contact" className="hover:opacity-80">Contact</a>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          {c.hero_image_url && <img src={resolveSrc(c.hero_image_url)} alt="" className="w-full h-full object-cover opacity-40" />}
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(0,0,0,0.4) 0%, ${accent} 100%)` }} />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 py-24 lg:py-32 text-center">
          <div className="text-[10px] tracking-[0.35em] uppercase mb-5" style={{ color: primary }}>Welcome</div>
          <h1 className="font-serif-display text-5xl lg:text-6xl leading-tight">{c.name}</h1>
          {ct.welcome_message && (
            <p className="text-neutral-300 mt-6 text-lg max-w-2xl mx-auto leading-relaxed">{ct.welcome_message}</p>
          )}
          {ct.service_times?.length > 0 && (
            <a href="#services" className="inline-block mt-10 px-7 py-3 rounded text-xs tracking-widest uppercase font-semibold" style={{ background: primary, color: accent }}>
              Join us this Sunday
            </a>
          )}
        </div>
      </section>

      {/* ABOUT / PASTOR */}
      {(c.pastor_name || ct.pastor_bio || c.pastor_photo_url) && (
        <section id="about" className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-12 items-center">
          <div>
            {c.pastor_photo_url ? (
              <img src={resolveSrc(c.pastor_photo_url)} alt={c.pastor_name} className="rounded-xl w-full max-w-md object-cover border border-white/10" />
            ) : (
              <div className="rounded-xl aspect-[4/5] flex items-center justify-center border border-white/10" style={{ background: "rgba(255,255,255,0.04)" }}>
                <Users size={64} className="text-neutral-700" />
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: primary }}>Our Pastor</div>
            <h2 className="font-serif-display text-4xl">{c.pastor_name}</h2>
            {ct.pastor_bio && <p className="text-neutral-300 mt-5 leading-relaxed">{ct.pastor_bio}</p>}
          </div>
        </section>
      )}

      {/* SERVICE TIMES */}
      {ct.service_times?.length > 0 && (
        <section id="services" className="py-20 border-t border-white/10">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-[10px] tracking-[0.3em] uppercase mb-3 text-center" style={{ color: primary }}>Services</div>
            <h2 className="font-serif-display text-4xl text-center">Join us in worship</h2>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {ct.service_times.map((s, i) => {
                const day = s.day_of_week || s.day;
                const start = s.start_time || s.time;
                const addr = composeAddress(s);
                return (
                  <div key={i} className="rounded-xl border border-white/10 p-6" style={{ background: "rgba(255,255,255,0.03)" }}>
                    <Clock size={20} style={{ color: primary }} />
                    <div className="font-serif-display text-xl mt-3">{s.name}</div>
                    <div className="text-sm text-neutral-300 mt-2">{day}{start && ` · ${fmtTime(start)}`}{s.end_time && `–${fmtTime(s.end_time)}`}</div>
                    {addr && <div className="text-xs text-neutral-500 mt-2 leading-relaxed">{addr}</div>}
                    {s.maps_url && <a href={s.maps_url} target="_blank" rel="noreferrer" className="text-[10px] tracking-widest uppercase mt-3 inline-block" style={{ color: primary }}>Open in Maps →</a>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* WEEKLY ACTIVITIES + DEPARTMENTS */}
      {(ct.weekly_activities?.length > 0 || ct.departments?.length > 0) && (
        <section id="activities" className="py-20 border-t border-white/10 grid md:grid-cols-2 gap-12 max-w-6xl mx-auto px-6">
          {ct.weekly_activities?.length > 0 && (
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: primary }}>Weekly</div>
              <h3 className="font-serif-display text-3xl">Activities</h3>
              <ul className="mt-6 space-y-4">
                {ct.weekly_activities.filter((a) => a.published !== false).map((a, i) => {
                  const day = a.day_of_week || a.day;
                  const start = a.start_time || a.time;
                  const addr = composeAddress(a);
                  return (
                    <li key={i} className="flex items-start gap-3 border-b border-white/5 pb-4">
                      <Calendar size={16} className="mt-1" style={{ color: primary }} />
                      <div className="flex-1">
                        <div className="text-sm font-semibold">{a.title || a.name}</div>
                        <div className="text-xs text-neutral-500 mt-1">{day}{start && ` · ${fmtTime(start)}`}{a.end_time && `–${fmtTime(a.end_time)}`}</div>
                        {addr && <div className="text-xs text-neutral-400 mt-1 leading-relaxed">{addr}</div>}
                        {a.description && <div className="text-xs text-neutral-400 mt-2 leading-relaxed">{a.description}</div>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {ct.departments?.length > 0 && (
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: primary }}>Departments</div>
              <h3 className="font-serif-display text-3xl">Serve with us</h3>
              <ul className="mt-6 space-y-3">
                {ct.departments.map((d, i) => (
                  <li key={i} className="border-b border-white/5 pb-3">
                    <div className="text-sm font-semibold">{d.name}</div>
                    {d.lead && <div className="text-xs text-neutral-500">Lead: {d.lead}</div>}
                    {d.description && <div className="text-xs text-neutral-400 mt-1">{d.description}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {/* SERMONS */}
      {ct.sermons?.length > 0 && (
        <section id="sermons" className="py-20 border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: primary }}>Library</div>
            <h2 className="font-serif-display text-4xl">Recent Sermons</h2>
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {ct.sermons.map((s, i) => (
                <a key={i} href={s.youtube_url || "#"} target="_blank" rel="noreferrer" className="rounded-xl border border-white/10 overflow-hidden group">
                  <div className="aspect-video flex items-center justify-center" style={{ background: "rgba(255,255,255,0.03)" }}>
                    {s.thumbnail_url ? <img src={resolveSrc(s.thumbnail_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <Tv size={36} className="text-neutral-600" />}
                  </div>
                  <div className="p-4">
                    <div className="font-serif-display text-lg">{s.title}</div>
                    {s.date && <div className="text-xs text-neutral-500 mt-1">{s.date}</div>}
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GALLERY */}
      {ct.gallery?.length > 0 && (
        <section className="py-20 border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: primary }}>Moments</div>
            <h2 className="font-serif-display text-4xl">Gallery</h2>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {ct.gallery.filter((g) => g.url).map((g, i) => (
                <div key={i} className="aspect-square overflow-hidden rounded-md">
                  <img src={resolveSrc(g.url)} alt={g.caption || ""} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* EVENTS */}
      {ct.events?.length > 0 && (
        <section id="events" className="py-20 border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: primary }}>What&apos;s On</div>
            <h2 className="font-serif-display text-4xl">Upcoming Events</h2>
            <div className="mt-8 space-y-5">
              {ct.events.filter((e) => e.published !== false).map((e, i) => {
                const addr = composeAddress(e);
                const recur = e.recurrence && e.recurrence !== "none" ? (e.recurrence === "custom" ? (e.recurrence_note || "custom") : e.recurrence) : null;
                return (
                  <div key={i} className="rounded-xl border border-white/10 p-5 flex flex-col md:flex-row md:items-stretch gap-5">
                    {e.image_url && <img src={resolveSrc(e.image_url)} alt="" className="w-full md:w-56 aspect-video object-cover rounded" />}
                    <div className="flex-1">
                      <div className="font-serif-display text-2xl">{e.title}</div>
                      <div className="text-xs text-neutral-400 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                        {e.date && <span className="inline-flex items-center gap-1"><Calendar size={12} style={{ color: primary }} /> {fmtDate(e.date)}</span>}
                        {(e.start_time || e.end_time) && <span className="inline-flex items-center gap-1"><Clock size={12} style={{ color: primary }} /> {fmtTime(e.start_time)}{e.end_time && `–${fmtTime(e.end_time)}`}</span>}
                        {recur && <span className="text-[10px] tracking-widest uppercase text-neutral-500">Recurs: {recur}</span>}
                      </div>
                      {addr && <div className="text-xs text-neutral-400 mt-3 inline-flex items-start gap-2 leading-relaxed"><MapPin size={12} style={{ color: primary }} className="mt-0.5" /> {addr}</div>}
                      {e.maps_url && <a href={e.maps_url} target="_blank" rel="noreferrer" className="text-[10px] tracking-widest uppercase mt-2 inline-block" style={{ color: primary }}>Open in Maps →</a>}
                      {e.description && <div className="text-sm text-neutral-300 mt-3 leading-relaxed">{e.description}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* LIVESTREAM */}
      {ct.livestream_url && (
        <section className="py-20 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: primary }}>Live</div>
            <h2 className="font-serif-display text-4xl">Watch with us</h2>
            <p className="text-neutral-400 mt-3">Join our service online.</p>
            <a href={ct.livestream_url} target="_blank" rel="noreferrer" className="inline-block mt-6 px-7 py-3 rounded text-xs tracking-widest uppercase font-semibold" style={{ background: primary, color: accent }}>
              Open Livestream
            </a>
          </div>
        </section>
      )}

      {/* CONTACT */}
      <section id="contact" className="py-20 border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-3 gap-8">
          {churchAddress && <div><MapPin size={20} style={{ color: primary }} /><div className="mt-3 text-sm text-neutral-300 leading-relaxed">{churchAddress}</div></div>}
          {c.phone && <div><Phone size={20} style={{ color: primary }} /><div className="mt-3 text-sm text-neutral-300">{c.phone}</div></div>}
          {c.email && <div><Mail size={20} style={{ color: primary }} /><div className="mt-3 text-sm text-neutral-300 break-all">{c.email}</div></div>}
        </div>
        {ct.social && (
          <div className="mt-10 flex justify-center gap-5">
            {ct.social.youtube && <a href={ct.social.youtube} target="_blank" rel="noreferrer"><Youtube size={20} className="text-neutral-400 hover:opacity-80" /></a>}
            {ct.social.facebook && <a href={ct.social.facebook} target="_blank" rel="noreferrer"><Facebook size={20} className="text-neutral-400 hover:opacity-80" /></a>}
            {ct.social.instagram && <a href={ct.social.instagram} target="_blank" rel="noreferrer"><Instagram size={20} className="text-neutral-400 hover:opacity-80" /></a>}
          </div>
        )}
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} {c.name} · Powered by <a href="/church-builder" className="hover:underline" style={{ color: primary }}>HOH Ministries Cloud</a>
      </footer>
    </div>
  );
}
